import GroupIcon from "@mui/icons-material/Group";
import HealthAndSafety from "@mui/icons-material/HealthAndSafety";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonIcon from "@mui/icons-material/Person";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { useColorScheme } from "@mui/material/styles";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { Icon, Typography } from "@platform/design-system";
import { KAIZEN_THEME } from "@platform/feature-flags";
import { JSX, useState } from "react";
import { useNavigate } from "react-router";
import { ROUTE_NAMES } from "@platform/types";

const EXIT_BROKER_VIEW = "Exit Broker View";
const BOTTOM_ITEMS = [EXIT_BROKER_VIEW, "Logout"];

type ColorModeMenuItemProps = {
  onSelect: () => void;
};

type UserMenuProps = {
  username: string;
  userType: string;
  logout: () => void;
  /** Present only while the user is impersonating a broker. */
  onExitBrokerView?: () => void;
  className: string;
};

type MenuItem = {
  label?: string;
  icon?: JSX.Element;
  roles?: string[];
  handler?: () => void | Promise<void>;
  colorMode?: boolean;
};

const ColorModeMenuItem: React.FC<ColorModeMenuItemProps> = ({ onSelect }) => {
  const { mode, setMode } = useColorScheme();
  const isDark = mode === "dark";
  const nextMode = isDark ? "light" : "dark";
  const label = isDark ? "Light mode" : "Dark mode";

  return (
    <MenuItem
      onClick={() => {
        setMode(nextMode);
        onSelect();
      }}
    >
      <ListItemIcon>
        <Icon name={isDark ? "lightMode" : "darkMode"} size="sm" />
      </ListItemIcon>
      <Typography textAlign="center">{label}</Typography>
    </MenuItem>
  );
};

const UserMenu: React.FC<UserMenuProps> = ({
  username = "",
  userType = "",
  logout = () => {},
  onExitBrokerView,
  className,
}) => {
  const navigate = useNavigate();
  const kaizenEnabled = useFeatureIsOn(KAIZEN_THEME);
  const [anchorElUser, setAnchorElUser] = useState<HTMLButtonElement | null>(
    null,
  );

  const settings = [
    {
      label: "Carriers Order",
      icon: <HealthAndSafety fontSize="small" />,
      roles: ["admin", "consultant"],
      handler: () => navigate(`/${ROUTE_NAMES.CARRIERS_ORDER}`),
    },
    {
      label: "Manage Users",
      icon: <GroupIcon fontSize="small" />,
      roles: ["broker"],
      handler: () => navigate("/users"),
    },
    {
      label: "Profile",
      icon: <PersonIcon fontSize="small" />,
      roles: ["admin", "broker", "agent", "analyst", "consultant", ""],
      handler: () => navigate("/profile"),
    },
    ...(onExitBrokerView
      ? [
          {
            label: EXIT_BROKER_VIEW,
            icon: <Icon name="back" size="sm" />,
            roles: ["admin", "broker", "agent", "analyst", "consultant", ""],
            handler: onExitBrokerView,
          },
        ]
      : []),
    {
      label: "Logout",
      icon: <LogoutIcon fontSize="small" />,
      roles: ["admin", "broker", "agent", "analyst", "consultant", ""],
      handler: logout,
    },
  ];

  const options = settings.filter((setting) =>
    setting.roles.includes(userType),
  );
  const isBottomItem = (setting: MenuItem) =>
    BOTTOM_ITEMS.includes(setting.label ?? "");
  const menuItems: MenuItem[] = [
    ...options.filter((setting) => !isBottomItem(setting)),
    ...(kaizenEnabled ? [{ colorMode: true }] : []),
    ...options.filter(isBottomItem),
  ];

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleCustomItems = (setting: MenuItem) => {
    handleCloseUserMenu();
    setting.handler?.();
  };

  return (
    <Box sx={{ flexGrow: 0 }} className={className}>
      <IconButton
        onClick={handleOpenUserMenu}
        sx={{ borderRadius: "5%", padding: "0.15rem" }}
      >
        <Typography variant="body-medium" ml={1}>
          <strong>{username}</strong>
        </Typography>
        {anchorElUser ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
      </IconButton>
      <Menu
        sx={{ mt: "45px" }}
        id="menu-appbar"
        anchorEl={anchorElUser}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        keepMounted
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        open={Boolean(anchorElUser)}
        onClose={handleCloseUserMenu}
      >
        {menuItems.map((setting) =>
          setting.colorMode ? (
            <ColorModeMenuItem
              key="color-mode"
              onSelect={handleCloseUserMenu}
            />
          ) : (
            <MenuItem
              key={setting.label}
              onClick={() => handleCustomItems(setting)}
            >
              <ListItemIcon>{setting.icon}</ListItemIcon>
              <Typography textAlign="center">{setting.label}</Typography>
            </MenuItem>
          ),
        )}
      </Menu>
    </Box>
  );
};

export { UserMenu };
